/**
 * Documents tab bucketing — splits a matter's files into the estate-only
 * "Core Documents" slots and the "Additional Documents" list beneath them.
 *
 * Extracted from `documents-tab-content.tsx` (VALE-769) so the split is
 * directly unit-testable: the rule decides what the tab displays, and the
 * sidebar badge is expected to agree with that total, so a silent change here
 * is a user-visible count discrepancy with nothing else watching it.
 */

import type {
  CoreDocumentSlot,
  CoreDocumentType,
  DocumentItem
} from '@/components/documents/v2';
import { getFileTypeSlug } from '@/lib/services/files/utils';
import type { MatterFile } from '@/lib/services/files/types';

const CORE_DOCUMENT_TYPES = [
  'will',
  'codicil',
  'death-certificate',
  'grant-of-representation',
  'grant-of-probate'
];

/**
 * The fixed slots the estate Core Documents section renders, in display order.
 *
 * Codicils are deliberately NOT here. A slot is "the one document of this kind",
 * and a matter may hold any number of codicils — so they are appended after the
 * will as one slot each (see `buildCodicilSlots`) rather than competing for a
 * single slot, which would render the first and hide the rest.
 */
const CORE_SLOTS: CoreDocumentSlot[] = [
  { type: 'death-certificate', label: 'Death Certificate' },
  { type: 'will', label: 'Will' },
  { type: 'grant-of-representation', label: 'Grant of Representation' }
];

/**
 * One core slot per codicil on the matter, oldest upload first.
 *
 * A codicil amends the will and is read together with it, so it belongs in Core
 * Documents — filed under Additional Documents it reads as incidental paperwork
 * rather than as part of the instrument set that decides who inherits.
 *
 * Returns an empty array for the ordinary single-will estate, which is what
 * keeps that matter's section byte-identical to before.
 */
function buildCodicilSlots(files: MatterFile[]): CoreDocumentSlot[] {
  return files
    .filter(
      (file) => getFileTypeSlug(file.fileType).toLowerCase() === 'codicil'
    )
    .map((file) => ({
      type: 'codicil' as CoreDocumentType,
      label: 'Codicil',
      document: toDocumentItem(file)
    }));
}

export function isCoreDocumentType(fileType: string): boolean {
  return CORE_DOCUMENT_TYPES.includes(fileType.toLowerCase());
}

/** Maps a backend file-type slug onto the core slot it can fill, if any. */
function mapToCoreType(fileType: string): CoreDocumentType | null {
  const normalized = fileType.toLowerCase();
  if (normalized === 'will') return 'will';
  // Never returns 'codicil': a codicil does not FILL one of the fixed slots, it
  // appends its own. Mapping it here would let it contend for — and lose — a slot.
  if (normalized === 'death-certificate') return 'death-certificate';
  if (
    normalized === 'grant-of-representation' ||
    normalized === 'grant-of-probate'
  ) {
    return 'grant-of-representation';
  }
  return null;
}

export function toDocumentItem(file: MatterFile): DocumentItem {
  return {
    id: file.id,
    name: file.displayName || file.originalFilename || 'Unnamed file',
    fileType: file.originalFilename?.split('.').pop() || 'pdf',
    category: isCoreDocumentType(getFileTypeSlug(file.fileType))
      ? 'core'
      : 'additional',
    uploadedAt: file.createdAt,
    sizeBytes: file.fileSizeKb ? file.fileSizeKb * 1024 : 0,
    isCertifiedCopy: file.isCertifiedCopy || false,
    mimeType: file.mimeType,
    hasThumbnail: true, // Assume thumbnails are available
    status: file.status,
    // The backend's own type slug, carried through alongside the extension.
    // The dense row names the type on the row and searches on it (VALE-404).
    typeSlug: getFileTypeSlug(file.fileType),
    // Undefined for every document that was read — including one read successfully
    // that had nothing to extract, which is the case this must stay distinct from.
    analysisFailureReasonCode: file.analysisFailure?.reasonCode,
    // Already resolved backend-side: the user's note wins over the analysis
    // sentence, so there is one line to carry and no precedence to re-derive here.
    notes: file.notes,
    // Carried alongside `name` rather than folded into it: `name` is the display
    // label, and the drawer shows both because they answer different questions.
    originalFilename: file.originalFilename
  };
}

export interface BucketMatterDocumentsArgs {
  /** Files returned by the matter-files endpoint (AVAILABLE only). */
  files: MatterFile[];
  /** Client-side overlay for uploads still processing, so they surface immediately. */
  pendingDocs: MatterFile[];
  /** Estate matters render the Core Documents section; others do not (LF-5436). */
  showCoreDocuments: boolean;
}

export interface BucketedMatterDocuments {
  coreDocuments: CoreDocumentSlot[];
  additionalDocuments: DocumentItem[];
}

/**
 * Every file lands in exactly one bucket, so `coreDocuments` (filled slots) plus
 * `additionalDocuments` always totals the files handed in. That total is what the
 * sidebar Documents badge counts, which is why nothing here may drop a file.
 */
export function bucketMatterDocuments({
  files,
  pendingDocs,
  showCoreDocuments
}: BucketMatterDocumentsArgs): BucketedMatterDocuments {
  // Merge in still-processing uploads that the AVAILABLE-only list hasn't
  // returned yet, so they surface immediately. A fetched row always wins over
  // its pending overlay (it carries the real status / thumbnail).
  const fetchedIds = new Set(files.map((f) => f.id));
  const mergedFiles = [
    ...files,
    ...pendingDocs.filter((p) => !fetchedIds.has(p.id))
  ];

  // Non-estate matters render no Core Documents section — surface every file
  // as an additional document (nothing hidden) and skip the core-slot work
  // (LF-5436).
  if (!showCoreDocuments) {
    return {
      coreDocuments: [],
      additionalDocuments: mergedFiles.map(toDocumentItem)
    };
  }

  // Each slot takes the first file that can fill it.
  const filledSlots = CORE_SLOTS.map((slot) => {
    const matchingFile = mergedFiles.find((file) => {
      const fileTypeSlug = getFileTypeSlug(file.fileType);
      return mapToCoreType(fileTypeSlug) === slot.type;
    });

    return {
      ...slot,
      document: matchingFile ? toDocumentItem(matchingFile) : undefined
    };
  });

  // Additional documents = everything a core slot did not already claim, excluded BY ID.
  //
  // Excluding by *file type* instead drops every file a slot couldn't hold: each slot takes
  // only the first match, so a matter with two wills — a codicil, a re-scanned original, an
  // updated will — rendered the first and displayed the rest nowhere at all. That regressed
  // in the v2 UI rewrite (100d6fa1a); the view it replaced excluded the three slot-filling
  // documents by id. Restoring that is also what lets the sidebar badge agree with this list,
  // since the badge counts every file the endpoint returns (VALE-769).
  const codicilSlots = buildCodicilSlots(mergedFiles);
  // The will's slot, then its codicils, then the grant — the order a lawyer reads
  // the instruments in. Splicing rather than appending keeps the grant last.
  const willIndex = filledSlots.findIndex((slot) => slot.type === 'will');
  const allCoreSlots =
    willIndex === -1
      ? [...filledSlots, ...codicilSlots]
      : [
          ...filledSlots.slice(0, willIndex + 1),
          ...codicilSlots,
          ...filledSlots.slice(willIndex + 1)
        ];

  const coreDocumentIds = new Set(
    allCoreSlots
      .map((slot) => slot.document?.id)
      .filter((id): id is number => id !== undefined)
  );
  const additionalDocuments = mergedFiles
    .filter((file) => !coreDocumentIds.has(file.id))
    .map(toDocumentItem);

  return { coreDocuments: allCoreSlots, additionalDocuments };
}
